(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const closeMenu = (menu) => {
    if (!menu?.open) return;
    menu.open = false;
  };

  const getCurrentPage = () => {
    const path = window.location.pathname
      .toLowerCase()
      .replace(/\/+$/, '')
      .replace(/\.html$/, '');

    if (!path || path === '/' || path.endsWith('/index')) return '';

    const pageAliases = [
      ['how-it-works', /(?:^|\/)how-it-works$/],
      ['memoirs', /(?:^|\/)memoirs$/],
      ['about', /(?:^|\/)about$/],
      ['pricing', /(?:^|\/)pricing$/],
      ['faq', /(?:^|\/)faq$/],
      ['consultation', /(?:^|\/)consultation$/],
    ];

    return pageAliases.find(([, pattern]) => pattern.test(path))?.[0] || '';
  };

  const updateCurrentPage = (root = document) => {
    const currentPage = getCurrentPage();

    root.querySelectorAll('.bw-nav-link[data-page], .bw-nav-action[data-page]').forEach((link) => {
      const isCurrent = Boolean(currentPage) && link.dataset.page === currentPage;
      link.classList.toggle('is-active', isCurrent);
      if (isCurrent) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const initializeHeader = () => {
    const header = document.querySelector('[data-bw-header]');
    if (header && !header.dataset.bwReady) {
      header.dataset.bwReady = 'true';
      let scrollFrame = 0;
      const updateScrolledState = () => {
        header.classList.toggle('is-scrolled', window.scrollY > 12);
      };
      const measureHeader = () => {
        document.documentElement.style.setProperty(
          '--bw-header-offset',
          `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`,
        );
      };
      const requestScrollUpdate = () => {
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          updateScrolledState();
        });
      };
      updateScrolledState();
      measureHeader();
      window.addEventListener('scroll', requestScrollUpdate, { passive: true });
      window.addEventListener('resize', () => {
        updateScrolledState();
        measureHeader();
        if (window.innerWidth > 940) {
          document.querySelectorAll('[data-bw-menu][open]').forEach((menu) => closeMenu(menu));
          document.body.classList.remove('bw-menu-open');
        }
      }, { passive: true });
    }

    document.querySelectorAll('[data-bw-menu]').forEach((menu) => {
      if (menu.dataset.bwReady) return;
      menu.dataset.bwReady = 'true';
      const summary = menu.querySelector('summary');
      const panel = menu.querySelector('.bw-menu__panel');
      const pageBackground = [
        document.querySelector('#MainContent'),
        document.querySelector('footer'),
      ].filter(Boolean);
      const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      let previouslyFocused = null;

      const getMenuFocusable = () => [summary, ...panel.querySelectorAll(focusableSelector)]
        .filter((element) => element && element.getClientRects().length > 0);

      const setPageInert = (isInert) => {
        pageBackground.forEach((element) => {
          element.inert = isInert;
        });
      };

      menu.addEventListener('toggle', () => {
        if (menu.open && header) {
          document.documentElement.style.setProperty(
            '--bw-header-offset',
            `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`,
          );
        }
        document.body.classList.toggle('bw-menu-open', menu.open);
        summary?.setAttribute('aria-expanded', String(menu.open));
        summary?.setAttribute('aria-label', menu.open ? 'Close navigation' : 'Open navigation');
        setPageInert(menu.open);

        if (menu.open) {
          previouslyFocused = document.activeElement;
          window.requestAnimationFrame(() => getMenuFocusable()[1]?.focus());
        } else if (previouslyFocused && document.contains(previouslyFocused)) {
          previouslyFocused.focus();
          previouslyFocused = null;
        }
      });

      menu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => closeMenu(menu));
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && menu.open) {
          closeMenu(menu);
          summary?.focus();
          return;
        }

        if (event.key === 'Tab' && menu.open) {
          const focusable = getMenuFocusable();
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });
    });

    updateCurrentPage();
  };

  const initializeReveal = (root = document) => {
    const heroRevealSelector = [
      '[data-bw-reveal="hero-copy"]',
      '[data-bw-reveal="hero-media"]',
    ].join(', ');
    const elements = [...root.querySelectorAll(heroRevealSelector)].filter(
      (element) => !element.dataset.bwRevealReady,
    );
    if (!elements.length) return;

    elements.forEach((element) => {
      element.dataset.bwRevealReady = 'true';
    });

    document.documentElement.classList.add('bw-motion-ready');

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const initialElements = elements.filter(
      (element) => element.getBoundingClientRect().top < window.innerHeight * 0.9,
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        initialElements.forEach((element) => element.classList.add('is-visible'));
      });
    });

    const observer = new IntersectionObserver(
      (entries, revealObserver) => {
        entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 },
    );

    elements
      .filter((element) => !initialElements.includes(element))
      .forEach((element) => observer.observe(element));
  };

  const initializeProcess = (root = document) => {
    root.querySelectorAll('[data-bw-process]').forEach((process) => {
      if (process.dataset.bwReady) return;
      process.dataset.bwReady = 'true';
      const steps = [...process.querySelectorAll('[data-bw-step]')];
      if (!steps.length) return;

      const activate = (activeStep) => {
        steps.forEach((step) => step.classList.toggle('is-active', step === activeStep));
      };

      activate(steps[0]);
      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        steps.forEach((step) => step.classList.add('is-active'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (visible[0]) activate(visible[0].target);
        },
        { rootMargin: '-28% 0px -48% 0px', threshold: [0, 0.2, 0.5, 0.8] },
      );

      steps.forEach((step) => observer.observe(step));
    });
  };

  const initializeAccordions = (root = document) => {
    root.querySelectorAll('.bwf-faq__list').forEach((list) => {
      if (list.dataset.bwReady) return;
      list.dataset.bwReady = 'true';
      list.querySelectorAll('details').forEach((detail) => {
        detail.addEventListener('toggle', () => {
          if (!detail.open) return;
          list.querySelectorAll('details[open]').forEach((openDetail) => {
            if (openDetail !== detail) openDetail.open = false;
          });
        });
      });
    });
  };

  const initializeConsultation = (root = document) => {
    const serviceSelect = root.querySelector('[data-bw-service-select]');
    const utmFields = [...root.querySelectorAll('[data-bw-utm]')];
    if (!serviceSelect && !utmFields.length) return;

    const params = new URLSearchParams(window.location.search);
    const requestedService = params.get('service');
    const matchingOption = serviceSelect
      ? [...serviceSelect.options].find((option) => option.dataset.serviceKey === requestedService)
      : null;

    if (matchingOption) {
      matchingOption.selected = true;
      serviceSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    utmFields.forEach((field) => {
      const value = params.get(field.dataset.bwUtm);
      if (value) field.value = value.slice(0, 120);
    });
  };

  const initialize = (root = document) => {
    initializeHeader();
    initializeReveal(root);
    initializeProcess(root);
    initializeAccordions(root);
    initializeConsultation(root);
    root.querySelector('.bw-form-message[autofocus]')?.focus();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
